import linkedinIcon from "../assets/images/social/linkedin.svg";
import xIcon from "../assets/images/social/x-twitter.svg";
import facebookIcon from "../assets/images/social/facebook.svg";
import instagramIcon from "../assets/images/social/instagram.svg";
import threadsIcon from "../assets/images/social/threads.svg";
import redditIcon from "../assets/images/social/reddit.svg";
import { MediaMetadata } from "../hooks/use-post-social";

export const X_STANDARD_MAX_CHARACTERS = 280;
export const X_PREMIUM_MAX_CHARACTERS = 25000;

export const SOCIAL_PLATFORMS = [
  { id: "linkedin", name: "LinkedIn", icon: linkedinIcon },
  { id: "x", name: "X (Twitter)", icon: xIcon },
  { id: "threads", name: "Threads", icon: threadsIcon },
  { id: "facebook", name: "Facebook", icon: facebookIcon },
  { id: "instagram", name: "Instagram", icon: instagramIcon },
  { id: "reddit", name: "Reddit", icon: redditIcon },
];

export function normalizeSocialPlatformId(platform: string | null | undefined): string | null {
  if (!platform) {
    return null;
  }

  const normalized = platform.trim().toLowerCase().replace(/[^a-z0-9]+/g, "");

  if (normalized === "linkedin" || normalized === "linkedln") {
    return "linkedin";
  }

  if (
    normalized === "x" ||
    normalized === "twitter" ||
    normalized === "xtwitter" ||
    normalized === "twitterx"
  ) {
    return "x";
  }

  if (normalized === "fb") {
    return "facebook";
  }

  return normalized;
}

export function normalizeSocialPlatformIds(
  platforms: Array<string | null | undefined> | null | undefined
): string[] {
  const seen = new Set<string>();
  return (platforms ?? []).reduce<string[]>((acc, platform) => {
    const normalized = normalizeSocialPlatformId(platform);
    if (!normalized || seen.has(normalized)) {
      return acc;
    }
    seen.add(normalized);
    acc.push(normalized);
    return acc;
  }, []);
}

const _twitterReqs = {
  maxImages: 4,
  maxVideos: 1,
  maxImageSizeBytes: 5 * 1024 * 1024,
  maxVideoSizeBytes: 512 * 1024 * 1024,
  videoDurationSeconds: 140,
  maxCharacters: X_STANDARD_MAX_CHARACTERS,
  minWidth: 4,
  minHeight: 4,
  maxWidth: 8192,
  maxHeight: 8192,
  allowedAspectRatios: null as { min: number; max: number }[] | null,
} as const;

export const PLATFORM_REQUIREMENTS = {
  twitter: _twitterReqs,
  x: _twitterReqs,
  threads: {
    ..._twitterReqs,
    maxCharacters: 500,
  },
  instagram: {
    maxImages: 10,
    maxVideos: 1,
    maxImageSizeBytes: 8 * 1024 * 1024,
    maxVideoSizeBytes: 300 * 1024 * 1024,
    videoDurationSeconds: 900,
    maxCharacters: 2200,
    maxHashtags: 30,
    minWidth: 320,
    minHeight: 320,
    maxWidth: 1936,
    maxHeight: 1936,
    allowedAspectRatios: [{ min: 0.8, max: 1.91 }],
  },
  facebook: {
    maxImages: 10,
    maxVideos: 1,
    maxImageSizeBytes: 10 * 1024 * 1024,
    maxVideoSizeBytes: 100 * 1024 * 1024,
    videoDurationSeconds: 1200,
    maxCharacters: 63206,
    minWidth: 200,
    minHeight: 200,
    maxWidth: 8192,
    maxHeight: 8192,
    allowedAspectRatios: null as { min: number; max: number }[] | null,
  },
  linkedin: {
    maxImages: 20,
    maxVideos: 1,
    maxImageSizeBytes: 5 * 1024 * 1024,
    maxVideoSizeBytes: 5 * 1024 * 1024 * 1024,
    videoDurationSeconds: 600,
    maxCharacters: 3000,
    minWidth: 552,
    minHeight: 276,
    maxWidth: 8192,
    maxHeight: 8192,
    allowedAspectRatios: null as { min: number; max: number }[] | null,
  },
  reddit: {
    maxImages: 1,
    maxVideos: 0,
    maxImageSizeBytes: 20 * 1024 * 1024,
    maxVideoSizeBytes: 0,
    videoDurationSeconds: 0,
    maxCharacters: 40000,
    minWidth: null as number | null,
    minHeight: null as number | null,
    maxWidth: null as number | null,
    maxHeight: null as number | null,
    allowedAspectRatios: null as { min: number; max: number }[] | null,
  },
} as const;

export type PlatformId = keyof typeof PLATFORM_REQUIREMENTS;

export function isXPlatformId(platform: string): boolean {
  return normalizeSocialPlatformId(platform) === "x";
}

/**
 * Fetches a URL (blob or http(s)) and returns a File. Use when you only have
 * a preview URL and need to send it as a file to uploadMedia.
 */
export async function urlToFile(
  url: string,
  name: string,
  index: number
): Promise<File> {
  const response = await fetch(url);
  const blob = await response.blob();
  const ext = blob.type.split("/")[1] || "bin";
  const fileName = `${name}-${index}.${ext}`;
  return new File([blob], fileName, { type: blob.type });
}

export function validateTextContent(
  platform: string,
  text: string,
  options?: { maxCharacters?: number | null }
): { isValid: boolean; errors: string[] } {
  const requirements = PLATFORM_REQUIREMENTS[platform as PlatformId];
  if (!requirements) return { isValid: true, errors: [] };

  const errors: string[] = [];
  const defaultMaxChars = (requirements as Record<string, unknown>)
    .maxCharacters as number | undefined;
  const maxChars =
    options && "maxCharacters" in options
      ? options.maxCharacters
      : defaultMaxChars;

  if (maxChars && text.length > maxChars) {
    errors.push(
      `Text exceeds ${maxChars.toLocaleString()} character limit (${text.length.toLocaleString()} chars)`
    );
  }

  if (platform === "instagram") {
    const maxHashtags = (
      requirements as Record<string, unknown>
    ).maxHashtags as number | undefined;
    if (maxHashtags) {
      const hashtagCount = (text.match(/#\w+/g) || []).length;
      if (hashtagCount > maxHashtags) {
        errors.push(
          `Too many hashtags (${hashtagCount}/${maxHashtags} max)`
        );
      }
    }
  }

  return { isValid: errors.length === 0, errors };
}

export function getMaxCharacters(platform: string): number | null {
  const requirements = PLATFORM_REQUIREMENTS[platform as PlatformId];
  if (!requirements) return null;
  return (
    ((requirements as Record<string, unknown>).maxCharacters as number) ?? null
  );
}

export function validatePlatform(
  platform: string,
  metadata: MediaMetadata[],
  options?: { isStory?: boolean }
): { isValid: boolean; errors: string[] } {
  const requirements = PLATFORM_REQUIREMENTS[platform as PlatformId];
  if (!requirements) {
    return { isValid: false, errors: ["Unknown platform"] };
  }

  const errors: string[] = [];
  const images = metadata.filter((m) => m.type === "image");
  const videos = metadata.filter((m) => m.type === "video");
  const skipAspectRatioForStory =
    platform === "instagram" && options?.isStory === true;

  // Instagram requires at least one image or video (no text-only posts)
  if (platform === "instagram" && metadata.length === 0) {
    errors.push("At least one image or video is required");
  }

  if (images.length > requirements.maxImages) {
    errors.push(
      `Maximum ${requirements.maxImages} image${
        requirements.maxImages > 1 ? "s" : ""
      } allowed`
    );
  }

  if (videos.length > requirements.maxVideos) {
    errors.push(
      requirements.maxVideos === 0
        ? "Videos are not supported"
        : `Maximum ${requirements.maxVideos} video${
            requirements.maxVideos > 1 ? "s" : ""
          } allowed`
    );
  }

  images.forEach((img, idx) => {
    if (img.size > requirements.maxImageSizeBytes) {
      errors.push(
        `Image ${idx + 1} exceeds maximum size of ${(
          requirements.maxImageSizeBytes /
          (1024 * 1024)
        ).toFixed(0)} MB`
      );
    }
  });

  videos.forEach((video, idx) => {
    if (
      requirements.maxVideoSizeBytes > 0 &&
      video.size > requirements.maxVideoSizeBytes
    ) {
      errors.push(
        `Video ${idx + 1} exceeds maximum size of ${(
          requirements.maxVideoSizeBytes /
          (1024 * 1024)
        ).toFixed(0)} MB`
      );
    }
  });

  if (requirements.minWidth != null && requirements.minHeight != null) {
    images.forEach((img, idx) => {
      if (
        img.width < requirements.minWidth! ||
        img.height < requirements.minHeight!
      ) {
        errors.push(
          `Image ${idx + 1} dimensions are below minimum (${
            requirements.minWidth
          } × ${requirements.minHeight} px)`
        );
      }
      if (
        requirements.maxWidth != null &&
        requirements.maxHeight != null &&
        (img.width > requirements.maxWidth ||
          img.height > requirements.maxHeight)
      ) {
        errors.push(
          `Image ${idx + 1} dimensions exceed maximum (${
            requirements.maxWidth
          } × ${requirements.maxHeight} px)`
        );
      }
    });
  }

  if (requirements.allowedAspectRatios && !skipAspectRatioForStory) {
    images.forEach((img, idx) => {
      const isValidRatio = requirements.allowedAspectRatios!.some(
        (range) => img.aspectRatio >= range.min && img.aspectRatio <= range.max
      );
      if (!isValidRatio) {
        errors.push(`Image ${idx + 1} has unsupported aspect ratio`);
      }
    });
  }

  videos.forEach((video, idx) => {
    if (
      video.duration != null &&
      video.duration > requirements.videoDurationSeconds
    ) {
      errors.push(
        `Video ${idx + 1} exceeds maximum duration of ${(
          requirements.videoDurationSeconds / 60
        ).toFixed(0)} minutes`
      );
    }
  });

  return { isValid: errors.length === 0, errors };
}

export const MAX_IMAGES_MAP: Record<string, number> = {
  twitter: 4,
  x: 4,
  threads: 4,
  instagram: 10,
  facebook: 10,
  linkedin: 20,
  reddit: 1,
};

export const MAX_VIDEOS_MAP: Record<string, number> = {
  twitter: 1,
  x: 1,
  threads: 1,
  instagram: 1,
  facebook: 1,
  linkedin: 1,
  reddit: 0,
};
